import { IonAlert, IonButton, IonFooter, useIonRouter } from '@ionic/react';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { t } from 'ttag';

import TabLayout from '@components/TabLayout';
import RoutePath from '@custom-types/RoutePath';
import { usePrinter } from '@hooks/usePrinter';
import { useScanner } from '@hooks/useScanner';
import { generateRoute } from '@services/navigation-service';
import { CapacitorBarcodeScannerCameraDirection } from '~capacitor-plugins/barcode-scanner';

const Footer = styled(IonFooter)`
  background-color: var(--ion-background-color, #fff);
`;

export const Scan = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const { push: pushRoute } = useIonRouter();

  const {
    result: scanResult,
    scan,
    reset: resetScanResult,
  } = useScanner({
    cameraDirection: CapacitorBarcodeScannerCameraDirection.BACK,
  });
  const { needsSetup: needsPrinterSetup } = usePrinter();
  const [showPrinterSetupError, setShowPrinterSetupError] = useState(false);

  const goToPrinterSettings = useCallback(
    () => pushRoute(generateRoute(RoutePath.PrinterSettings, { eventId })),
    [pushRoute, eventId]
  );

  useEffect(() => {
    if (!scanResult) return;

    if (needsPrinterSetup) {
      setShowPrinterSetupError(true);
      goToPrinterSettings();
      return;
    }
    resetScanResult();
    pushRoute(
      generateRoute(RoutePath.PrintBadgeFlow, {
        eventId,
        checkInCode: scanResult,
      })
    );
  }, [eventId, goToPrinterSettings, needsPrinterSetup, pushRoute, scanResult, resetScanResult]);

  useEffect(() => {
    if (needsPrinterSetup) {
      goToPrinterSettings();
    }
  }, [goToPrinterSettings, needsPrinterSetup]);

  return (
    <TabLayout title={t`Print Badges only`}>
      <Footer>
        <IonButton expand="block" onClick={scan}>{t`Scan`}</IonButton>
        <IonButton
          expand="block"
          fill="outline"
          role="button"
          onClick={goToPrinterSettings}
          color={needsPrinterSetup ? 'danger' : 'primary'}
        >
          {t`Printer Settings`}
        </IonButton>
        <IonAlert
          isOpen={showPrinterSetupError}
          // skipcq: JS-0417
          onDidDismiss={() => setShowPrinterSetupError(false)}
          header={t`Printer Setup Required`}
          message={t`Please set up your printer before scanning badges.`}
          buttons={[t`OK`]}
        />
      </Footer>
    </TabLayout>
  );
};
